// run-command-prompt-mission-decision-ledger-section.ts
//
// Phase 06f R4 (dogfood F-32, spec E30): the clean reviewer T-9002 asked, in
// remaining_work, to "check the decision log" it had no access to. A review
// prompt (kind `review`: review-local-v1 AND review-clean-v1) now carries the
// mission's settled choices, read-only. C2 redefined (câu treo 1): the clean
// session still never sees the implement side's NARRATIVE — the question text
// (often "I did not write X because…") stays out, so does the decision time and
// any user id (sources print `người` / `delegated:<G0 id>` as in the MR block).
// The title deliberately avoids "Sổ quyết định" (the C2 test's marker for the
// `review.md` playbook leaking into the clean session).
//
// Pure: the caller (supervisor-loop.ts) reads the ledger with
// `readDecisionLedger`, which already masks the mission's known values.
import {
  decisionChoiceAndSource,
  decisionCodeAndTicket,
  oneLine,
} from "./decision-ledger-merge-request-block.js";
import type { DecisionLedger, DecisionLedgerEntry } from "./decision-ledger-projection.js";

export const MISSION_DECISION_LEDGER_SECTION_TITLE = "## Lựa chọn đã chốt trong mission (chỉ đọc)";
export const MISSION_DECISION_LEDGER_MAX_ENTRIES = 20;
const LABEL_MAX_CHARS = 120;
const LEAD =
  "- Quyết định có thẩm quyền (người hoặc ủy nhiệm G0) đã chốt trong mission. Đối chiếu code với lựa chọn này; không cần đi tìm interview.json/journal, không ghi remaining_work đòi xác nhận lại mục đã có ở đây.";

/**
 * Label cut here (N9), not in the shared MR helper: one line first, then ≤ 120
 * code points (review 06f L5: a UTF-16 `slice` could split an emoji's
 * surrogate pair and leave a lone half in the prompt).
 */
function withClippedLabel(e: DecisionLedgerEntry): DecisionLedgerEntry {
  const label = oneLine(e.optionLabel);
  const points = Array.from(label);
  const clipped =
    points.length <= LABEL_MAX_CHARS ? label : `${points.slice(0, LABEL_MAX_CHARS - 1).join("")}…`;
  return { ...e, optionLabel: clipped };
}

/**
 * `""` when the ledger has no entry (prompt byte-identical to before).
 * Otherwise `\n<title>\n<lead>\n- QĐ-NN [T-x] → <choice> — nguồn <source>…`,
 * at most the 20 newest entries plus a pointer line; no trailing newline (the golden I1
 * filter drops the section by its title, like E15/E19).
 */
export function renderMissionDecisionLedgerSection(ledger: DecisionLedger): string {
  if (ledger.entries.length === 0) return "";
  // Review 06f L1: the 20 NEWEST (entries are in decision order) — the review
  // checks the latest code, which the latest choices are closest to.
  const shown = ledger.entries.slice(-MISSION_DECISION_LEDGER_MAX_ENTRIES);
  const lines = shown.map((entry) => {
    const e = withClippedLabel(entry);
    return `- ${oneLine(`${decisionCodeAndTicket(e)} → ${decisionChoiceAndSource(e)}`)}`;
  });
  const rest = ledger.entries.length - shown.length;
  if (rest > 0) lines.push(`- … (+${rest} mục, xem avatar autopilot status --decisions)`);
  return ["", MISSION_DECISION_LEDGER_SECTION_TITLE, LEAD, ...lines].join("\n");
}
